import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    image_url: '',
    excerpt: ''
  });

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch blog posts');
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/blog`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog post created successfully');
      setIsDialogOpen(false);
      setFormData({
        title: '',
        content: '',
        author: '',
        image_url: '',
        excerpt: ''
      });
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-heading font-bold text-maroon">Blog Posts</h2>
        <Button onClick={() => setIsDialogOpen(true)} className="btn-primary" data-testid="add-blog-btn">
          <Plus size={20} className="mr-2" />
          Add Blog Post
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <div key={post.id} className="card flex items-center justify-between" data-testid="blog-item">
            <div className="flex items-center space-x-4">
              <img src={post.image_url} alt={post.title} className="w-24 h-24 object-cover rounded-sm border border-maroon/10" />
              <div>
                <h3 className="font-heading font-bold text-xl text-maroon">{post.title}</h3>
                <p className="text-maroon/70 text-sm">By {post.author} • {new Date(post.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <Button 
              onClick={() => handleDelete(post.id)} 
              variant="destructive" 
              size="sm"
              data-testid="delete-blog-btn"
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">Add Blog Post</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="blog-form">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="input-field"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="author">Author *</Label>
                <Input
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  required
                  className="input-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url">Image URL *</Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  className="input-field"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt *</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief summary of the post..."
                required
                rows={2}
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Full blog post content..."
                required
                rows={12}
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              />
            </div>

            <Button type="submit" className="btn-primary w-full">
              Create Blog Post
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;